import { maintenancePointToCatalogItem } from 'pages/Assets/maintenance/maintenancePointUtils'
import type { ApiStationRow } from 'pages/Robot/RobotView/RobotLeftBlock/Service/serviceCalibApi'
import type { ServiceCalibCatalogItem } from 'pages/Robot/RobotView/RobotLeftBlock/Service/serviceCalibTypes'
import { KIND_STATION_LOT } from 'pages/Robot/RobotView/RobotLeftBlock/Service/serviceCalibUtils'
import type { Point } from 'geojson'
import { IMaintenancePoint } from 'utils/interfaces'
import { IServicePoint, SERVICE_POINT_KINDS } from 'utils/servicePointsCatalog'

/** Facility lot row for scenario/mission SP map (type may be missing on raw API rows). */
export type FacilityLotRow = {
  id: number
  name: string
  lot_id: number
  type?: number
  lot_model?: string | null
  robot_station?: number | null
  robot_station_hardware_id?: string | null
}

function stationPoint(station: ApiStationRow | undefined): Point | null {
  const point = station?.point
  if (!point?.coordinates || point.coordinates.length < 2) {
    return null
  }
  return point
}

function findStationForLot(
  lot: FacilityLotRow,
  stationsByPk: Map<number, ApiStationRow>,
  stationsByHardwareId: Map<string, ApiStationRow>
): ApiStationRow | undefined {
  if (lot.robot_station != null) {
    const byPk = stationsByPk.get(lot.robot_station)
    if (byPk) return byPk
  }
  const hardwareId = lot.robot_station_hardware_id?.trim()
  if (hardwareId) {
    return stationsByHardwareId.get(hardwareId)
  }
  return undefined
}

function lotToCatalogItem(lot: FacilityLotRow, station: ApiStationRow | undefined): ServiceCalibCatalogItem | null {
  const lotModel = lot.lot_model?.trim()
  const hardwareId = (lot.robot_station_hardware_id || station?.station_id || '').trim()
  const point = stationPoint(station)
  if (!lotModel || !hardwareId || !point) {
    return null
  }
  return {
    key: `lot-${lot.id}`,
    name: lot.name,
    target: 'station_lot',
    point,
    metadata: {
      kind: KIND_STATION_LOT,
      station_id: hardwareId,
      lot_id: lot.lot_id,
      lot_model: lotModel,
    },
    stationHardwareId: hardwareId,
    stationName: station?.name,
    lotId: lot.lot_id,
    lotType: lot.type,
  }
}

/**
 * Map scenario/mission service_points[] to ServiceCalib map pins.
 * wait steps are omitted; station_lot uses RobotStation.point; MP uses MaintenancePoint.point.
 */
export function scenarioServicePointsToCatalogItems(
  servicePoints: IServicePoint[],
  facilityLots: FacilityLotRow[],
  maintenancePoints: IMaintenancePoint[],
  stations: ApiStationRow[]
): ServiceCalibCatalogItem[] {
  const stationsByPk = new Map(stations.map(s => [s.id, s]))
  const stationsByHardwareId = new Map<string, ApiStationRow>()
  stations.forEach(station => {
    if (station.station_id) {
      stationsByHardwareId.set(station.station_id, station)
    }
  })

  const lotsByPk = new Map(facilityLots.map(lot => [lot.id, lot]))
  const lotsByStationAndLotId = new Map(
    facilityLots.map(lot => [`${lot.robot_station_hardware_id || ''}:${lot.lot_id}`, lot])
  )

  const items: ServiceCalibCatalogItem[] = []
  const seenKeys = new Set<string>()

  for (const sp of servicePoints) {
    if (sp.kind === SERVICE_POINT_KINDS.WAIT) {
      continue
    }

    if (sp.kind === SERVICE_POINT_KINDS.STATION_LOT) {
      let lot: FacilityLotRow | undefined
      if (sp.lot_pk != null) {
        lot = lotsByPk.get(sp.lot_pk)
      }
      if (!lot && sp.station_id != null && sp.lot_id != null) {
        lot = lotsByStationAndLotId.get(`${sp.station_id}:${sp.lot_id}`)
      }
      if (!lot) {
        continue
      }
      const station = findStationForLot(lot, stationsByPk, stationsByHardwareId)
      const item = lotToCatalogItem(lot, station)
      if (!item || seenKeys.has(item.key)) {
        continue
      }
      seenKeys.add(item.key)
      items.push(item)
      continue
    }

    if (sp.kind === SERVICE_POINT_KINDS.MAINTENANCE_POINT) {
      const mp = maintenancePoints.find(p => p.facility_index === sp.maintenance_point_id)
      if (!mp) {
        continue
      }
      const item = maintenancePointToCatalogItem(mp)
      if (!item?.point || seenKeys.has(item.key)) {
        continue
      }
      seenKeys.add(item.key)
      items.push(item)
    }
  }

  return items
}

export function catalogKeyForServicePoint(
  sp: IServicePoint,
  facilityLots: FacilityLotRow[],
  maintenancePoints: IMaintenancePoint[]
): string | null {
  if (sp.kind === SERVICE_POINT_KINDS.STATION_LOT) {
    if (sp.lot_pk != null) {
      return `lot-${sp.lot_pk}`
    }
    const lot = facilityLots.find(
      l => l.robot_station_hardware_id === sp.station_id && l.lot_id === sp.lot_id
    )
    return lot ? `lot-${lot.id}` : null
  }
  if (sp.kind === SERVICE_POINT_KINDS.MAINTENANCE_POINT) {
    const mp = maintenancePoints.find(p => p.facility_index === sp.maintenance_point_id)
    return mp ? `mp-${mp.id}` : null
  }
  return null
}
